import { ElementTypes } from '../types/element';
import {
  BackgroundType,
  BorderType,
  HorizontalAlignment,
  ImageDisplayType,
  ImageSizingType,
  ISpace,
  ListStyleType,
  TableModeType,
  VerticalAlignment
} from '../types/space';

export const INITIAL_BACKGROUND_CONFIG = {
  background: BackgroundType.Transparent,
  backgroundColor: '#FFFFFF',
  padding: 0,
  borderRadius: 0,
};
export const INITIAL_FONT_CONFIG = {
  font: 'Arial',
  fontSize: 14,
  fontWeight: '400',
  lineHeight: '1.4',
  horizontalAlign: HorizontalAlignment.Left,
  verticalAlign: VerticalAlignment.Top,
  textColor: '#000000',
};
const INITIAL_IMAGE_CONFIG = {
  imageDisplay: ImageDisplayType.First,
  imageSizing: ImageSizingType.Fit,
  imageSize: 200,
  imagePadding: 0,
};
export const INITIAL_TABLE_CONFIG = {
  borderStyle: BorderType.All,
  listStyle: ListStyleType.Bullets,
  headingStyle: {
    ...INITIAL_FONT_CONFIG,
    fontSize: 16,
    fontWeight: '500',
    horizontalAlign: HorizontalAlignment.Center,
  },
  showHeadings: true,
  showRefAsText: false,
  tableMode: TableModeType.Table,
  tableSorting: [],
  tableColumns: [],
  rowStyle: INITIAL_FONT_CONFIG,
};
const INITIAL_CONTENT_CONFIG = {
  content: '',
};
export const INITIAL_SPACE_CONFIG = {
  id: '',
  posX: 200,
  posY: 100,
  width: 200,
  height: 50,
  rotation: 0,
  order: 0,
  isAvoidCollision: false,
};

// Static
const STATIC_TEXT_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.StaticText,
  config: { ...INITIAL_BACKGROUND_CONFIG, ...INITIAL_FONT_CONFIG, ...INITIAL_CONTENT_CONFIG },
};
const STATIC_ATTACHMENT_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.StaticAttachment,
  config: { ...INITIAL_BACKGROUND_CONFIG, imageContent: {} },
};

// Field text, long text, date time, id, etc.
const FIELD_TEXT_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldText,
  config: { ...INITIAL_BACKGROUND_CONFIG, ...INITIAL_FONT_CONFIG },
};
const FIELD_NUMBER_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldNumber,
};
const FIELD_PHONE_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldPhone,
};
const FIELD_EMAIL_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldEmail,
};
const FIELD_CURRENCY_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldCurrency,
};
const FIELD_COLLABORATOR_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldCollaborator,
};
const FIELD_LONG_TEXT_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  isAvoidCollision: false,
  type: ElementTypes.FieldLongText,
};
const FIELD_DATE_TIME_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldDateTime,
};
const FIELD_AUTO_NUMBER_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldAutoNumber,
};
const FIELD_FORMULA_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldFormula,
};
const FIELD_ROLLUP_INITIAL_CONFIG = {
  ...FIELD_TEXT_INITIAL_CONFIG,
  type: ElementTypes.FieldRollup,
};

// Multi select fields, ratings
const FIELD_SELECT_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldSelect,
  config: { ...INITIAL_BACKGROUND_CONFIG },
};

const FIELD_STATUS_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldStatus,
  config: { ...INITIAL_BACKGROUND_CONFIG },
};
const FIELD_MULTI_SELECT_INITIAL_CONFIG = {
  ...FIELD_SELECT_INITIAL_CONFIG,
  type: ElementTypes.FieldMultiSelect,
};
const FIELD_RATING_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldRating,
  config: { ...INITIAL_BACKGROUND_CONFIG },
};
const FIELD_CHECKBOX_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldCheckbox,
  config: { ...INITIAL_BACKGROUND_CONFIG },
};

// Attachment
const FIELD_MULTI_ATTACH_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldMultiAttachment,
  config: { ...INITIAL_BACKGROUND_CONFIG, ...INITIAL_IMAGE_CONFIG },
};

// Record reference - or table
const FIELD_RECORD_REF_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  isAvoidCollision: false,
  height: 120,
  type: ElementTypes.FieldRecordRef,
  config: {
    ...INITIAL_BACKGROUND_CONFIG,
    ...INITIAL_TABLE_CONFIG,
  },
};

const FIELD_SUBTABLE_INITIAL_CONFIG = {
  ...FIELD_RECORD_REF_INITIAL_CONFIG,
  type: ElementTypes.FieldSubtable,
};

// Lookup will have everything put together
const FIELD_LOOKUP_INITIAL_CONFIG = {
  ...INITIAL_SPACE_CONFIG,
  type: ElementTypes.FieldLookup,
  config: {
    ...INITIAL_BACKGROUND_CONFIG,
    ...INITIAL_TABLE_CONFIG,
    ...INITIAL_IMAGE_CONFIG,
    ...INITIAL_FONT_CONFIG,
  },
};

export const ELEMENT_INITIAL_CONFIG: { [key in ElementTypes]: ISpace<ElementTypes> } = {
  [ElementTypes.StaticText]: STATIC_TEXT_INITIAL_CONFIG,
  [ElementTypes.StaticAttachment]: STATIC_ATTACHMENT_INITIAL_CONFIG,
  [ElementTypes.FieldText]: FIELD_TEXT_INITIAL_CONFIG,
  [ElementTypes.FieldNumber]: FIELD_NUMBER_INITIAL_CONFIG,
  [ElementTypes.FieldEmail]: FIELD_EMAIL_INITIAL_CONFIG,
  [ElementTypes.FieldPhone]: FIELD_PHONE_INITIAL_CONFIG,
  [ElementTypes.FieldCurrency]: FIELD_CURRENCY_INITIAL_CONFIG,
  [ElementTypes.FieldCollaborator]: FIELD_COLLABORATOR_INITIAL_CONFIG,
  [ElementTypes.FieldLongText]: FIELD_LONG_TEXT_INITIAL_CONFIG,
  [ElementTypes.FieldDateTime]: FIELD_DATE_TIME_INITIAL_CONFIG,
  [ElementTypes.FieldAutoNumber]: FIELD_AUTO_NUMBER_INITIAL_CONFIG,
  [ElementTypes.FieldFormula]: FIELD_FORMULA_INITIAL_CONFIG,
  [ElementTypes.FieldRollup]: FIELD_ROLLUP_INITIAL_CONFIG,
  [ElementTypes.FieldStatus]: FIELD_STATUS_INITIAL_CONFIG,
  [ElementTypes.FieldSelect]: FIELD_SELECT_INITIAL_CONFIG,
  [ElementTypes.FieldMultiSelect]: FIELD_MULTI_SELECT_INITIAL_CONFIG,
  [ElementTypes.FieldMultiAttachment]: FIELD_MULTI_ATTACH_INITIAL_CONFIG,
  [ElementTypes.FieldRating]: FIELD_RATING_INITIAL_CONFIG,
  [ElementTypes.FieldCheckbox]: FIELD_CHECKBOX_INITIAL_CONFIG,
  [ElementTypes.FieldRecordRef]: FIELD_RECORD_REF_INITIAL_CONFIG,
  [ElementTypes.FieldLookup]: FIELD_LOOKUP_INITIAL_CONFIG,
  [ElementTypes.FieldUnsupported]: FIELD_TEXT_INITIAL_CONFIG,
  [ElementTypes.FieldSubtable]: FIELD_SUBTABLE_INITIAL_CONFIG,
};

export const ELEMENT_EDIT_TEXT: { [key in ElementTypes]?: { title: string } } = {
  [ElementTypes.StaticText]: {
    title: 'Text',
  },
  [ElementTypes.StaticAttachment]: {
    title: 'Image',
  },
};
